/**
 * Main Content Area Component
 * 
 * Displays the main content based on the selected node in the selection tree.
 * Updated with unified design tokens.
 */

import React, { useMemo } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import MappingTable from './MappingTable';
import ProcessConditionsSection from './ProcessConditionsSection';
import ProjectPropertiesSection from './ProjectPropertiesSection';
import AdvancedSettingsSection from './AdvancedSettingsSection';
import GeneralAttributesSection from './GeneralAttributesSection';
import StatusBuilderSection from './StatusBuilderSection';
import StatusCategorySection from './StatusCategorySection';
import { useMappingConfigurationContext } from '../MappingConfigurationContext';
import { useRevitParameterOptions } from '../../../../shared/hooks/useRevitParameterOptions';
import { toRevitParameterType } from '../../../../shared/lib/revit/parameterOptionTypeFilter';
import { mappingOverviewTooltips } from '../../../../shared/config/mappingHelpCatalog';
import type {
  ScopeEditorPersistenceState,
  ScopeEditorPersistenceStateUpdater,
} from '../../lib/scopeEditorPersistence';
import WorkspaceMobileHeader from '../../../../shared/ui/workflow/WorkspaceMobileHeader';
import {
  useConnectorBindingConfiguration,
  useStatusBuilderConfiguration,
} from '../../../../shared/stores/useConfigurationStore';
import { getSelectedScopeLabel } from '../../lib/selectionTreeModel';
import MappingEmptyState from './MappingEmptyState';
interface MainContentAreaProps {
  selectedNode: { type: string; name?: string };
  highlightedAttributeNames?: string[];
  mappingCanvasRef?: React.RefObject<HTMLDivElement | null>;
  tableControlsRef?: React.Ref<HTMLTableRowElement>;
  pcfColumnRef?: React.RefObject<HTMLDivElement | null>;
  revitColumnRef?: React.RefObject<HTMLDivElement | null>;
  typeColumnRef?: React.RefObject<HTMLDivElement | null>;
  formulaColumnRef?: React.RefObject<HTMLDivElement | null>;
  rowActionsRef?: React.RefObject<HTMLDivElement | null>;
  generalSectionRef?: React.RefObject<HTMLDivElement | null>;
  processConditionsRef?: React.RefObject<HTMLDivElement | null>;
  projectParametersRef?: React.RefObject<HTMLDivElement | null>;
  advancedSectionRef?: React.RefObject<HTMLDivElement | null>;
  statusBuilderSectionRef?: React.RefObject<HTMLDivElement | null>;
  statusCategorySectionRef?: React.RefObject<HTMLDivElement | null>;
  processConditionsEditorState?: ScopeEditorPersistenceState;
  onProcessConditionsEditorStateChange?: (nextState: ScopeEditorPersistenceStateUpdater) => void;
  projectPropertiesEditorState?: ScopeEditorPersistenceState;
  onProjectPropertiesEditorStateChange?: (nextState: ScopeEditorPersistenceStateUpdater) => void;
  /** Callback to open mobile navigation menu */
  onMobileMenuToggle?: () => void;
  mobileMenuButtonRef?: React.RefObject<HTMLButtonElement | null>;
  /** Whether mobile menu is currently open */
  mobileMenuOpen?: boolean;
}

const REQUIRED_ATTRIBUTE_NAMES = ['OBJECT-TYPE', 'SKEY', 'PIPING-SPEC'];

const MainContentArea: React.FC<MainContentAreaProps> = ({
  selectedNode,
  highlightedAttributeNames = [],
  mappingCanvasRef,
  tableControlsRef,
  pcfColumnRef,
  revitColumnRef,
  typeColumnRef,
  formulaColumnRef,
  rowActionsRef,
  generalSectionRef,
  processConditionsRef,
  projectParametersRef,
  advancedSectionRef,
  statusBuilderSectionRef,
  statusCategorySectionRef,
  processConditionsEditorState,
  onProcessConditionsEditorStateChange,
  projectPropertiesEditorState,
  onProjectPropertiesEditorStateChange,
  onMobileMenuToggle,
  mobileMenuButtonRef,
  mobileMenuOpen = false,
}) => {
  const theme = useTheme();
  const isLaptop = useMediaQuery(theme.breakpoints.down('lg'));
  const selectedStatusCategoryName = useStatusBuilderConfiguration().categories.find(
    (category) => category.id === selectedNode.name
  )?.name;
  const connectorConfig = useConnectorBindingConfiguration();

  const {
    selectedObjectType,
    currentAttributes,
    revitParameterOptions,
    handleAttributeChange,
    handleRemoveAttribute,
    handleAddAttribute,
    // Advanced settings
    advancedSettings,
    handleCenterOfCoordinatesChange,
    // Handlers for process conditions
    processConditionsChecked,
    onProcessConditionsValueChange,
    onPipingSpecValuePCChange,
    onTracingSpecValuePCChange,
    onAttribute12ValueChange,
    onAttribute13ValueChange,
    onAttribute14ValueChange,
    onAttribute15ValueChange,
    onAttribute16ValueChange,
    onAttribute17ValueChange,
    onAttribute18ValueChange,
    onAttribute19ValueChange,
    onAttribute20ValueChange,
    onAttribute21ValueChange,
    onAttribute22ValueChange,
    onAttribute23ValueChange,
    // Handlers for project properties
    projectPropertiesChecked,
    onProjectPropertiesValueChange,
    onProjectIdentifierValueChange,
    onProjectNameValueChange,
    onAreaValueChange,
    onAttribute1ClientNameChange,
    onAttribute2ProjectAddressChange,
    onAttribute3ClientDepartmentChange,
    onAttribute4ClientProjectNumberChange,
    onAttribute5ClientDocumentNumberChange,
    onAttribute6ClientCodeChange,
    onAttribute7EngPartyOfficeChange,
    onAttribute8EngPartyOfficeLocationChange,
    onAttribute9EngPartyProjectNumberChange,
    onAttribute10EngPartyAreaCodeChange,
    // Values for process conditions and project properties
    processConditionsValue,
    projectPropertiesValue,
    // Process conditions attributes
    pipingSpecValuePC,
    tracingSpecValuePC,
    attribute12Value,
    attribute13Value,
    attribute14Value,
    attribute15Value,
    attribute16Value,
    attribute17Value,
    attribute18Value,
    attribute19Value,
    attribute20Value,
    attribute21Value,
    attribute22Value,
    attribute23Value,
    // Project properties attributes
    projectIdentifierValue,
    projectNameValue,
    areaValue,
    attribute1ClientName,
    attribute2ProjectAddress,
    attribute3ClientDepartment,
    attribute4ClientProjectNumber,
    attribute5ClientDocumentNumber,
    attribute6ClientCode,
    attribute7EngPartyOffice,
    attribute8EngPartyOfficeLocation,
    attribute9EngPartyProjectNumber,
    attribute10EngPartyAreaCode,
    // Accessibility helpers
    getAttributeAriaProps,
  } = useMappingConfigurationContext();

  // General attributes values from context
  const {
    skeyValue,
    objectTypeNameValue,
    pipingSpecValue,
    systemGroupingValue,
    insulationSpecValue,
    tracingSpecValue,
    weightValue,
    insulationThicknessValue,
    // General attribute handlers
    onSkeyValueChange,
    onObjectTypeNameValueChange,
    onPipingSpecValueChange,
    onSystemGroupingValueChange,
    onInsulationSpecValueChange,
    onTracingSpecValueChange,
    onWeightValueChange,
    onInsulationThicknessValueChange,
  } = useMappingConfigurationContext();

  const { options: liveRevitParameterOptions } = useRevitParameterOptions({
    enabled: selectedNode.type === 'objectType',
    preferredParameterType: toRevitParameterType(connectorConfig.parameterBindingKind),
  });
  const mappingTableParameterOptions = useMemo(
    () => [
      ...liveRevitParameterOptions.map((option) => ({
        ...option,
        group: option.group || 'Revit project parameters',
      })),
      ...revitParameterOptions,
    ],
    [liveRevitParameterOptions, revitParameterOptions]
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      role="region"
      aria-label="Selected PCF mapping editor"
    >
      {onMobileMenuToggle && (
        <WorkspaceMobileHeader
          title={getSelectedScopeLabel(selectedNode, selectedStatusCategoryName)}
          onMenuToggle={onMobileMenuToggle}
          menuButtonRef={mobileMenuButtonRef}
          mobileMenuOpen={mobileMenuOpen}
          help={mappingOverviewTooltips.currentSection}
          dataTestId="mapping-workspace-mobile-header"
        />
      )}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {selectedNode.type === 'objectType' && selectedNode.name ? (
          <Box
            ref={mappingCanvasRef}
            sx={{
              flex: 1,
              overflow: 'hidden',
              border: 'none',
            }}
          >
            <MappingTable
              selectedObjectType={selectedObjectType}
              currentAttributes={currentAttributes}
              revitParameterOptions={mappingTableParameterOptions}
              onAttributeChange={handleAttributeChange}
              onRemoveAttribute={handleRemoveAttribute}
              onAddAttribute={handleAddAttribute}
              getAttributeAriaProps={getAttributeAriaProps}
              dense={isLaptop}
              requiredAttributeNames={REQUIRED_ATTRIBUTE_NAMES}
              highlightedAttributeNames={highlightedAttributeNames}
              tableControlsRef={tableControlsRef}
              pcfColumnRef={pcfColumnRef}
              revitColumnRef={revitColumnRef}
              typeColumnRef={typeColumnRef}
              formulaColumnRef={formulaColumnRef}
              rowActionsRef={rowActionsRef}
            />
          </Box>
        ) : selectedNode.type === 'general' ? (
          <Box ref={generalSectionRef} sx={{ flex: 1, minHeight: 0 }}>
            <GeneralAttributesSection
              skeyValue={skeyValue}
              objectTypeNameValue={objectTypeNameValue}
              pipingSpecValue={pipingSpecValue}
              systemGroupingValue={systemGroupingValue}
              insulationSpecValue={insulationSpecValue}
              tracingSpecValue={tracingSpecValue}
              weightValue={weightValue}
              insulationThicknessValue={insulationThicknessValue}
              onSkeyValueChange={onSkeyValueChange}
              onObjectTypeNameValueChange={onObjectTypeNameValueChange}
              onPipingSpecValueChange={onPipingSpecValueChange}
              onSystemGroupingValueChange={onSystemGroupingValueChange}
              onInsulationSpecValueChange={onInsulationSpecValueChange}
              onTracingSpecValueChange={onTracingSpecValueChange}
              onWeightValueChange={onWeightValueChange}
              onInsulationThicknessValueChange={onInsulationThicknessValueChange}
            />
          </Box>
        ) : selectedNode.type === 'processConditions' ? (
          <Box ref={processConditionsRef} sx={{ flex: 1, minHeight: 0 }} data-testid="mapping-tour-target-process-conditions-section">
            <ProcessConditionsSection
              enabled={processConditionsChecked}
              processConditionsValue={processConditionsValue}
              pipingSpecValuePC={pipingSpecValuePC}
              tracingSpecValuePC={tracingSpecValuePC}
              attribute12Value={attribute12Value}
              attribute13Value={attribute13Value}
              attribute14Value={attribute14Value}
              attribute15Value={attribute15Value}
              attribute16Value={attribute16Value}
              attribute17Value={attribute17Value}
              attribute18Value={attribute18Value}
              attribute19Value={attribute19Value}
              attribute20Value={attribute20Value}
              attribute21Value={attribute21Value}
              attribute22Value={attribute22Value}
              attribute23Value={attribute23Value}
              onProcessConditionsValueChange={onProcessConditionsValueChange}
              onPipingSpecValuePCChange={onPipingSpecValuePCChange}
              onTracingSpecValuePCChange={onTracingSpecValuePCChange}
              onAttribute12ValueChange={onAttribute12ValueChange}
              onAttribute13ValueChange={onAttribute13ValueChange}
              onAttribute14ValueChange={onAttribute14ValueChange}
              onAttribute15ValueChange={onAttribute15ValueChange}
              onAttribute16ValueChange={onAttribute16ValueChange}
              onAttribute17ValueChange={onAttribute17ValueChange}
              onAttribute18ValueChange={onAttribute18ValueChange}
              onAttribute19ValueChange={onAttribute19ValueChange}
              onAttribute20ValueChange={onAttribute20ValueChange}
              onAttribute21ValueChange={onAttribute21ValueChange}
              onAttribute22ValueChange={onAttribute22ValueChange}
              onAttribute23ValueChange={onAttribute23ValueChange}
              editorState={processConditionsEditorState}
              onEditorStateChange={onProcessConditionsEditorStateChange}
            />
          </Box>
        ) : selectedNode.type === 'projectProperties' ? (
          <Box ref={projectParametersRef} sx={{ flex: 1, minHeight: 0 }} data-testid="mapping-tour-target-project-parameters-section">
            <ProjectPropertiesSection
              enabled={projectPropertiesChecked}
              projectPropertiesValue={projectPropertiesValue}
              projectIdentifierValue={projectIdentifierValue}
              projectNameValue={projectNameValue}
              areaValue={areaValue}
              attribute1ClientName={attribute1ClientName}
              attribute2ProjectAddress={attribute2ProjectAddress}
              attribute3ClientDepartment={attribute3ClientDepartment}
              attribute4ClientProjectNumber={attribute4ClientProjectNumber}
              attribute5ClientDocumentNumber={attribute5ClientDocumentNumber}
              attribute6ClientCode={attribute6ClientCode}
              attribute7EngPartyOffice={attribute7EngPartyOffice}
              attribute8EngPartyOfficeLocation={attribute8EngPartyOfficeLocation}
              attribute9EngPartyProjectNumber={attribute9EngPartyProjectNumber}
              attribute10EngPartyAreaCode={attribute10EngPartyAreaCode}
              onProjectPropertiesValueChange={onProjectPropertiesValueChange}
              onProjectIdentifierValueChange={onProjectIdentifierValueChange}
              onProjectNameValueChange={onProjectNameValueChange}
              onAreaValueChange={onAreaValueChange}
              onAttribute1ClientNameChange={onAttribute1ClientNameChange}
              onAttribute2ProjectAddressChange={onAttribute2ProjectAddressChange}
              onAttribute3ClientDepartmentChange={onAttribute3ClientDepartmentChange}
              onAttribute4ClientProjectNumberChange={onAttribute4ClientProjectNumberChange}
              onAttribute5ClientDocumentNumberChange={onAttribute5ClientDocumentNumberChange}
              onAttribute6ClientCodeChange={onAttribute6ClientCodeChange}
              onAttribute7EngPartyOfficeChange={onAttribute7EngPartyOfficeChange}
              onAttribute8EngPartyOfficeLocationChange={onAttribute8EngPartyOfficeLocationChange}
              onAttribute9EngPartyProjectNumberChange={onAttribute9EngPartyProjectNumberChange}
              onAttribute10EngPartyAreaCodeChange={onAttribute10EngPartyAreaCodeChange}
              editorState={projectPropertiesEditorState}
              onEditorStateChange={onProjectPropertiesEditorStateChange}
            />
          </Box>
        ) : selectedNode.type === 'advanced' ? (
          <Box ref={advancedSectionRef} sx={{ flex: 1, minHeight: 0 }} data-testid="mapping-tour-target-advanced-mapping-section">
            <AdvancedSettingsSection
              advancedSettings={advancedSettings}
              onCenterOfCoordinatesChange={handleCenterOfCoordinatesChange}
            />
          </Box>
        ) : selectedNode.type === 'statusBuilder' ? (
          <Box ref={statusBuilderSectionRef} sx={{ flex: 1, minHeight: 0 }} data-testid="mapping-tour-target-status-builder-section">
            <StatusBuilderSection />
          </Box>
        ) : selectedNode.type === 'statusCategory' && selectedNode.name ? (
          <Box
            ref={statusCategorySectionRef}
            sx={{ flex: 1, minHeight: 0 }}
            data-testid="mapping-tour-target-status-category-section"
          >
            <StatusCategorySection categoryId={selectedNode.name} />
          </Box>
        ) : (
          <MappingEmptyState />
        )}
      </Box>
    </Box>
  );
};

export default MainContentArea;
